// src/services/hrCalendarService.js
// =============================================================================
// HR calendar and work-pattern API service
// =============================================================================
//
// PURPOSE
// -------
// This module is the thin frontend API layer for Leave / HR master data that is
// broader than a single employee:
// - facility calendar exceptions,
// - reusable rotating work pattern templates.
//
// It follows the same architectural rule as other hb_visits services:
// - apiClient owns auth/session headers and refresh handling,
// - this file only calls endpoints and normalizes small shape differences,
// - UI components own presentation and error messages.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[hrCalendarService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "ID_MISSING";
    throw e;
  }
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

async function getList(path, params = {}) {
  try {
    const res = await apiClient.get(path, { withCredentials: true, params });
    return asArray(res?.data);
  } catch (err) {
    throwApiError(`GET ${path}`, err);
  }
}

async function post(path, payload) {
  try {
    const res = await apiClient.post(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`POST ${path}`, err);
  }
}

async function put(path, payload) {
  try {
    const res = await apiClient.put(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PUT ${path}`, err);
  }
}

async function patch(path, payload) {
  try {
    const res = await apiClient.patch(path, payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PATCH ${path}`, err);
  }
}

// Facility calendar exceptions ---------------------------------------------

export const listCalendarExceptions = (params = {}) =>
  getList("/hr/calendar-exceptions", params);

export const createCalendarException = (payload) =>
  post("/hr/calendar-exceptions", payload);

export const updateCalendarException = (id, payload) => {
  assertId("updateCalendarException", id);
  return put(`/hr/calendar-exceptions/${id}`, payload);
};

export const setCalendarExceptionActive = (id, active) => {
  assertId("setCalendarExceptionActive", id);
  return patch(`/hr/calendar-exceptions/${id}/active`, { active: Boolean(active) });
};

// Work pattern templates -----------------------------------------------------

export const listWorkPatterns = (params = {}) =>
  getList("/hr/work-patterns", params);

export const createWorkPattern = (payload) =>
  post("/hr/work-patterns", payload);

export const updateWorkPattern = (id, payload) => {
  assertId("updateWorkPattern", id);
  return put(`/hr/work-patterns/${id}`, payload);
};

export const setWorkPatternActive = (id, isActive) => {
  assertId("setWorkPatternActive", id);
  return patch(`/hr/work-patterns/${id}/active`, { is_active: Boolean(isActive) });
};

export default {
  listCalendarExceptions,
  createCalendarException,
  updateCalendarException,
  setCalendarExceptionActive,
  listWorkPatterns,
  createWorkPattern,
  updateWorkPattern,
  setWorkPatternActive,
};
